import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { z } from "zod";

import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import ContentEditorField from "components/ContentEditor/ContentEditorField";
import { DangerButton } from "components/atoms/DangerButton";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { ErrorWrapper } from "components/organisms/form/BaseField";
import Form from "components/organisms/form/Form";
import FormButton from "components/organisms/form/FormButton";
import MultiSelectField from "components/organisms/form/MultiSelectField";
import TextField from "components/organisms/form/TextField";
import ToggleField from "components/organisms/form/ToggleField";
import { useDeleteVilDuViteMerMutation, useGetVilDuViteMerQuery, useUpsertVilDuViteMerMutation } from "pages/Content/VilDuViteMer/VilDuViteMer.graphql-gen";

import { ContentLinkType, HelpPageLocation } from "generated/graphql-types";

const VilDuViteMerSchema = z.object({
	published: z.boolean(),
	locations: z.array(z.string()),
	image: z
		.object({
			url: z.string().optional(),
			reference: z.string().optional(),
			provider: z.string().optional(),
		})
		.optional(),
	link: z
		.object({
			url: z.string().optional(),
			type: z.nativeEnum(ContentLinkType).optional(),
			view: z.string().optional(),
			viewType: z.string().optional(),
		})
		.optional(),
	title: z.string().optional(),
	content: z
		.object({
			html: z.string().optional(),
			json: z.string().optional(),
		})
		.optional(),
	order: z.coerce.number(),
});

export default function UpsertVilDuViteMerPage() {
	const { id } = useParams<{ id?: string }>();
	const navigate = useNavigate();
	const [upsertHelpPage, { loading, error }] = useUpsertVilDuViteMerMutation();
	const [deleteHelpPage, { loading: deleteLoading }] = useDeleteVilDuViteMerMutation();
	const { data } = useGetVilDuViteMerQuery({
		variables: {
			helpPageId: id ?? "",
		},
		skip: !id,
	});

	return (
		<div>
			<MainLayoutPageHeader title="Ny vil du vite mer-artikkel" />
			<Flex gap="16px">
				<Form
					key={data?.singleHelpPage?.id}
					loading={loading}
					value={
						data?.singleHelpPage ?? {
							content: {
								json: "",
								html: "",
							},
						}
					}
					onSubmit={async (newData) => {
						const id = data?.singleHelpPage?.id ?? uuid();

						const locations = newData.locations as HelpPageLocation[];

						const { error } = await upsertHelpPage({
							variables: {
								helpPage: {
									id,
									...newData,
									locations,
								},
							},
						});

						if (id === data?.singleHelpPage?.id) {
							return;
						}

						!error && navigate(`/vil-du-vite-mer/${id}`);
					}}
					schema={VilDuViteMerSchema}
				>
					<Flex direction={Direction.column} gap="16px" style={{ paddingTop: "16px" }}>
						<ToggleField name="published" label="Publisert?" />
						<TextField name="title" label="Tittel" />
						<ContentEditorField name="content" label="Innhold" />
						<TextField name="order" label="Rekkefølge" type="number" />
						<MultiSelectField
							name="locations"
							label="Lokasjon"
							options={Object.keys(HelpPageLocation).map((l) => ({
								label: l,
								value: l,
							}))}
						/>
						<div>
							<TextField name="image.url" label="Bilde" />
							<TextField name="image.reference" type="hidden" value="" />
							<TextField name="image.provider" type="hidden" value="" />
						</div>

						{error && <ErrorWrapper>{error.message}</ErrorWrapper>}
						<FormButton loading={loading} label={`${id ? "Oppdater" : "Opprett ny"} artikkel`} />
						{data?.singleHelpPage?.id && (
							<DangerButton
								loading={deleteLoading}
								label="Slett artikkel"
								onClick={async () => {
									if (!window.confirm("Sikker på at du ønsker å slette " + data.singleHelpPage?.title)) {
										return;
									}
									await deleteHelpPage({
										variables: {
											helpPageId: data?.singleHelpPage?.id ?? "",
										},
									});

									navigate(`/vil-du-vite-mer`);
								}}
							/>
						)}
					</Flex>
				</Form>
			</Flex>
		</div>
	);
}
